import { liveSessionManager } from '@saucelabs/sl-util-axios';
import Sentry from '@saucelabs/sl-util-sentry';
import {
  getOSNameAndVersion,
  displayNamesMap,
  isMobileDevice,
} from '@saucelabs/sl-util-helpers';

import lsmConstants from './constants';
import testConfigurationConstants from '../testConfiguration/constants';
import * as constants from './../../constants';
import { utils as testConfigurationUtils } from '../testConfiguration';

const {
  CONFIGURATION_DEVICE_TYPES: { DESKTOP_BROWSER, VIRTUAL_DEVICE },
  CONFIGURATION_TEST_TYPES,
} = constants;

const { DEFAULT_TUNNEL_NAME } = lsmConstants;
const { ANDROID, IOS, LINUX, MAC } = testConfigurationConstants.PLATFORMS;
const {
  [ANDROID]: ANDROID_PRETTY,
  [IOS]: IOS_PRETTY,
  [LINUX]: LINUX_PRETTY,
  [MAC]: MAC_PRETTY,
} = testConfigurationConstants.PLATFORMS_PRETTY;

/**
 * Validates the shape of formData passed to createCapabilitiesFromFormData()
 * Makes sure it contains the properties necessary to create a payload object for a session creation
 * Throws errors if deviceType, url, browserConfiguration or virtualDeviceConfiguration are invalid
 * @param {object} formData
 * @returns {Void}
 */
const validateCapabilitiesFormData = (formData) => {
  const validDeviceTypes = [DESKTOP_BROWSER, VIRTUAL_DEVICE];

  if (!validDeviceTypes.includes(formData.deviceType)) {
    throw new Error(`"deviceType" property is required and must be either "${DESKTOP_BROWSER}" or "${VIRTUAL_DEVICE}"`);
  }

  if (formData.testType === 'APP') {
    if (!formData.mobileAppConfiguration?.id) {
      throw new Error('no app id present in "mobileAppConfiguration"');
    }
  } else {
    try {
      // eslint-disable-next-line no-unused-vars
      const validUrl = new URL(formData.url);
    } catch (error) {
      throw new Error('"url" property is required and needs to be a valid URL');
    }
  }

  if (formData.deviceType === DESKTOP_BROWSER && !formData.browserConfiguration) {
    throw new Error(`"browserConfiguration" property is required when deviceType is ${DESKTOP_BROWSER}`);
  }

  if (formData.deviceType === VIRTUAL_DEVICE && !formData.virtualDeviceConfiguration) {
    throw new Error(`"virtualDeviceConfiguration" property is required when deviceType is ${VIRTUAL_DEVICE}`);
  }
};

const createTunnelCapabilities = (tunnel, user) => {
  const { tunnelOwner, tunnelName } = testConfigurationUtils.parseTunnelNameString(tunnel);
  return {
    tunnelName,
    tunnelOwner: tunnelOwner !== user ? tunnelOwner : null,
  };
};

/**
 * Creates a capabilities object necessary to start a virtual session
 * @param {object} formData
 * @returns {object}
 */
const createCapabilitiesFromFormData = (formData, user) => {
  validateCapabilitiesFormData(formData);
  const { deviceType, mobileAppConfiguration, tunnel = null, testType, url } = formData;

  let tunnelCapabilities = {
    tunnelName: null,
    tunnelOwner: null,
  };

  if (tunnel) {
    tunnelCapabilities = createTunnelCapabilities(tunnel, user);
  }

  const isAppTest = testType === CONFIGURATION_TEST_TYPES.APP;
  const commonCapabilities = {
    // TODO investigate tunnel functionality to make sure we pass the correct data
    // TODO update tests accordingly
    ...tunnelCapabilities,
    ...(isAppTest ? { app: `storage:${mobileAppConfiguration.id}` } : { url }),
  };

  if (deviceType === DESKTOP_BROWSER) {
    const { browserConfiguration } = formData;
    return {
      platformName: browserConfiguration.os, // required
      browserName: browserConfiguration.browser, // required
      browserVersion: browserConfiguration.version, // required
      screenResolution: browserConfiguration.res || null,
      ...commonCapabilities,
    };
  }

  if (deviceType === VIRTUAL_DEVICE) {
    const { virtualDeviceConfiguration } = formData;

    return {
      // This data is not available in formData
      platformName: virtualDeviceConfiguration.browser === ANDROID ? ANDROID_PRETTY : IOS_PRETTY, // required
      platformVersion: virtualDeviceConfiguration.version, // required
      deviceName: virtualDeviceConfiguration.device, // required
      // Property does not exist in the frontend yet.
      // see https://gitlab.tools.saucelabs.net/live/live-session-manager#post-v1lsmsession
      ...commonCapabilities,
    };
  }

  return {};
};

/* NOTE: this code will be used on response from /rest/v1/manual/{id} endpoint */
const getDisplayMetaFromSession = (payload) => {
  if (isMobileDevice(payload.browser)) {
    return {
      browserDisplay: payload.browserDisplay,
      browserVersionDisplay: payload.browserVersionDisplay,
      osDisplay: displayNamesMap[payload.osDisplay] || payload.osDisplay,
      osVersionDisplay: payload.osVersionDisplay,
    };
  }
  const { name, version } = getOSNameAndVersion(payload.os);
  return {
    browserDisplay: payload.browserDisplay,
    browserVersionDisplay: payload.browserVersionDisplay,
    osDisplay: name,
    osVersionDisplay: version,
  };
};

const getDisplayMetaFromAppSession = (appStorageFile) => {
  const configuration = appStorageFile?.metadata ?? {};

  return {
    appIcon: appStorageFile?.getIconUrl(),
    appName: configuration.name,
    appVersion: configuration.version,
    appVersionCode: configuration.version_code,
  };
};

/**
 * Returns session metadata depending on the device type
 * @param {object} formData
 * @returns {object}
 */
const getMetaFromFormData = (formData) => {
  const { tunnelOwner, tunnelName } = testConfigurationUtils.parseTunnelNameString(formData.tunnel);
  const tunnel = tunnelOwner ? `${tunnelOwner}/${tunnelName || DEFAULT_TUNNEL_NAME}` : null;

  if (formData?.deviceType === DESKTOP_BROWSER) {
    const configuration = formData.browserConfiguration;
    const displayMeta = getDisplayMetaFromSession(configuration);

    return {
      ...configuration,
      ...displayMeta,
      isMobileVirtual: false,
      platform: displayMeta.osDisplay?.toLowerCase(),
      tunnel,
      url: formData.url,
    };
  }

  if (
    formData?.testType === CONFIGURATION_TEST_TYPES.APP &&
    formData?.deviceType === VIRTUAL_DEVICE
  ) {
    const configuration = formData.mobileAppConfiguration;
    const displayMeta = getDisplayMetaFromAppSession(configuration);
    const platform = configuration.kind === ANDROID ? ANDROID_PRETTY : IOS_PRETTY;
    const {
      browserDisplay,
      browserVersionDisplay,
      osVersionDisplay,
    } = formData?.virtualDeviceConfiguration;

    return {
      ...configuration,
      ...displayMeta,
      browserDisplay,
      browserVersionDisplay,
      filename: configuration.name,
      isMobileVirtual: true,
      kind: configuration.kind,
      osDisplay: platform,
      osVersionDisplay,
      platform: platform.toLowerCase(),
      tunnel,
    };
  }
  if (formData?.deviceType === VIRTUAL_DEVICE) {
    const configuration = formData.virtualDeviceConfiguration;
    const displayMeta = getDisplayMetaFromSession(configuration);
    const platform = configuration.browser === ANDROID ? ANDROID_PRETTY : IOS_PRETTY;

    return {
      ...configuration,
      ...displayMeta,
      isMobileVirtual: true,
      osDisplay: platform,
      platform: platform.toLowerCase(),
      tunnel,
      url: formData.url,
    };
  }

  return {};
};

/**
 * Returns session metadata mapped from LSM capabilities
 * @param {object} capabilities
 * @returns {object}
 */
const getMetaFromRemoteCapabilities = ({
  capabilities,
  owner,
  system_info: systemInfo,
  tunnel,
}) => {
  const tunnelName = tunnel ?
    `${capabilities.tunnelOwner || (owner.username)}/${capabilities.tunnelName || DEFAULT_TUNNEL_NAME}` :
    null;
  const configuration = {
    app: capabilities.app,
    browser: capabilities.browserName,
    browserDisplay: displayNamesMap[capabilities.browserName?.toLowerCase()],
    browserVersionDisplay: capabilities.browserVersion,
    name: capabilities.name,
    os: capabilities.platformName,
    res: capabilities.screenResolution,
    tunnel: tunnelName,
    url: capabilities.url,
    version: capabilities.browserVersion,
  };
  const displayMeta = getDisplayMetaFromSession(configuration);

  if (!capabilities.deviceName) {
    return {
      ...configuration,
      ...displayMeta,
      isMobileVirtual: false,
      platform: displayMeta.osDisplay?.toLowerCase(),
    };
  }

  const os = capabilities.platformName === ANDROID_PRETTY ? LINUX_PRETTY : MAC_PRETTY;
  const osDisplay = capabilities.platformName === ANDROID_PRETTY ? LINUX_PRETTY : MAC;
  return {
    ...configuration,
    ...displayMeta,
    browser: capabilities.platformName?.toLowerCase(),
    browserDisplay: capabilities.deviceName,
    browserVersionDisplay: capabilities.browserVersion ? capabilities.browserVersion : '',
    isMobileVirtual: true,
    os,
    osDisplay,
    osVersionDisplay: systemInfo?.os || capabilities.platformVersion,
    platform: capabilities.platformName?.toLowerCase(),
    version: capabilities.platformVersion,
  };
};

/**
 * Return the absolute URL to the LSM socket based on the current environment
 * @param {string} sessionId - session_id from session creation
 * @returns {(string|undefined)}
 */
const createSocketUrl = (sessionId = '') => {
  try {
    const lsmUrl = new URL(liveSessionManager.defaults.baseURL);
    const protocol = (lsmUrl.protocol === 'https:') ? 'wss:' : 'ws:';
    return `${protocol}//${lsmUrl.host}/v1/lsm/session/${sessionId}/channel`;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

/**
 * Return the absolute URL to the RFB socket based on the current environment
 * @param {string} rfbPath - relative path to RFB socket
 * @returns {(string|undefined)}
 */
const createRfbUrl = (rfbPath = '') => {
  try {
    const lsmUrl = new URL(liveSessionManager.defaults.baseURL);
    const protocol = (lsmUrl.protocol === 'https:') ? 'wss:' : 'ws:';
    return `${protocol}//${lsmUrl.host}${rfbPath}`;
  } catch (error) {
    Sentry.captureException(error);
    return undefined;
  }
};

/**
 * Create the filename of the screenshot based on the given metadata
 * @param {ImmutableMap} metadata - virtual session metadata
 * @returns {string} screenshot filename
 */
const createScreenshotName = (metadata) => {
  const { isMobileVirtual, browser, device, version } = metadata.toJS();
  const environment = isMobileVirtual ? device.replace(/\s/g, '') : browser;
  const dateTime = new Date().toISOString().substr(0, 19)
    .replace(/-/g, '')
    .replace('T', '_');

  return `${dateTime}_${environment}_${version}.png`;
};

export default {
  createCapabilitiesFromFormData,
  createRfbUrl,
  createScreenshotName,
  createSocketUrl,
  createTunnelCapabilities,
  getDisplayMetaFromAppSession,
  getDisplayMetaFromSession,
  getMetaFromFormData,
  getMetaFromRemoteCapabilities,
  validateCapabilitiesFormData,
};
